import { useState, useMemo, useCallback } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Send,
  Heart,
  CheckCircle2,
  Archive,
  User,
  Calendar,
  Eye,
  MoreHorizontal,
  ImageIcon,
  Sparkles,
  EyeOff,
  Grid3X3,
  Check,
  XCircle,
  Clock,
  ShoppingBag,
  Bell,
  RefreshCw,
  DollarSign,
  CalendarDays
} from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import AttendeeCardModal from "@/components/AttendeeCardModal";
import ImageLightbox from "@/components/ImageLightbox";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const PAYMENT_OPTIONS = [
  { key: "paid",                       label: "Paid",                        color: "bg-emerald-100 text-emerald-700" },
  { key: "payment_pending",            label: "Payment pending",             color: "bg-amber-100 text-amber-700" },
  { key: "completed_paid",             label: "Completed · paid",            color: "bg-green-100 text-green-800" },
  { key: "completed_unpaid",           label: "Completed · unpaid",          color: "bg-yellow-100 text-yellow-800" },
  { key: "refunded",                   label: "Refunded",                    color: "bg-stone-100 text-stone-600" },
];

const STAGES = [
  { key: "invited", label: "Invites Sent", icon: Send, color: "border-amber-300", headerBg: "bg-amber-50", badge: "bg-amber-100 text-amber-700", accent: "#d97706" },
  { key: "preferences_shared", label: "Preferences Shared", icon: Heart, color: "border-purple-300", headerBg: "bg-purple-50", badge: "bg-purple-100 text-purple-700", accent: "#7c3aed" },
  { key: "confirmed", label: "Confirmed", icon: CalendarDays, color: "border-blue-300", headerBg: "bg-blue-50", badge: "bg-blue-100 text-blue-700", accent: "#2563eb" },
  { key: "completed", label: "Complete", icon: CheckCircle2, color: "border-green-300", headerBg: "bg-green-50", badge: "bg-green-100 text-green-700", accent: "#16a34a" },
  { key: "archived", label: "Archive", icon: Archive, color: "border-gray-300", headerBg: "bg-gray-50", badge: "bg-gray-100 text-gray-600", accent: "#6b7280" },
];

function deriveStage(rsvp) {
  // Explicit appointment_stage wins (admin/host has manually moved the card)
  if (rsvp.appointment_stage && rsvp.appointment_stage !== "invited") return rsvp.appointment_stage;
  if (rsvp.status === "completed") return "completed";
  if (rsvp.status === "cant_make_it" || rsvp.status === "cancelled") return "archived";
  // Auto-derive Confirmed: Acuity appointment booked OR service+date locked OR payment recorded
  const hasService = !!(rsvp.service_id || rsvp.selected_service_id);
  const hasPayment = !!rsvp.payment_status;
  const hasAppointment = !!(rsvp.acuity_appointment_id || rsvp.appointment_date);
  if (rsvp.status === "confirmed" && (hasAppointment || (hasService && hasPayment))) return "confirmed";
  if (rsvp.preferences_shared) return "preferences_shared";
  return "invited";
}

function sortByDate(items) {
  return [...items].sort((a, b) => {
    const da = a.appointment_date || a.created_at || "";
    const db = b.appointment_date || b.created_at || "";
    if (!da && !db) return 0;
    if (!da) return 1;
    if (!db) return -1;
    return new Date(da) - new Date(db);
  });
}

function PrefChips({ prefs = {} }) {
  const chips = [];
  Object.entries(prefs).forEach(([key, val]) => {
    if (key === "notes" || key === "goals" || !val) return;
    if (Array.isArray(val)) {
      val.slice(0, 2).forEach(v => chips.push(v));
      if (val.length > 2) chips.push(`+${val.length - 2}`);
    } else if (typeof val === "string" && val.length < 25) {
      chips.push(val);
    }
  });
  if (chips.length === 0) return null;
  return (
    <div className="flex flex-wrap gap-0.5 mt-1.5">
      {chips.slice(0, 3).map((c, i) => (
        <span key={i} className="inline-block px-1.5 py-0.5 bg-gray-100 text-gray-600 text-[9px] rounded-full truncate max-w-[90px]">{c}</span>
      ))}
      {chips.length > 3 && <span className="text-[9px] text-gray-400">+{chips.length - 3}</span>}
    </div>
  );
}

function PreferenceDetail({ rsvp, onImageClick }) {
  const prefs = rsvp.preferences || rsvp.intake_answers || {};
  const inspo = rsvp.inspo_images || rsvp.inspiration_images || rsvp.inspo || [];
  const dateStr = rsvp.appointment_date
    ? new Date(rsvp.appointment_date).toLocaleDateString("en-US", { weekday: "short", month: "long", day: "numeric", year: "numeric" })
    : "Date TBD";

  const getImgUrl = (img) => typeof img === "string" ? img : (img.url || img.image_url || "");

  return (
    <div className="space-y-4" data-testid={`pref-detail-${rsvp.rsvp_id}`}>
      <div className="flex items-center gap-3">
        <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center shrink-0 overflow-hidden">
          {rsvp.profile_image ? (
            <img src={rsvp.profile_image} alt="" className="w-12 h-12 rounded-full object-cover" />
          ) : (
            <User className="w-5 h-5 text-gray-400" />
          )}
        </div>
        <div className="flex-1">
          <p className="text-sm font-semibold text-gray-900">{rsvp.name}</p>
          <p className="text-xs text-gray-500">{rsvp.email || ""}</p>
          {rsvp.service_name && <Badge variant="outline" className="text-[10px] mt-0.5">{rsvp.service_name}</Badge>}
        </div>
        <div className="text-right">
          <div className="flex items-center gap-1 text-xs text-gray-500">
            <Calendar className="w-3 h-3" />
            {dateStr}
          </div>
        </div>
      </div>

      {inspo.length > 0 && (
        <div>
          <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">Inspiration</p>
          <div className="grid grid-cols-3 gap-2">
            {inspo.map((img, i) => (
              <div key={i} className="aspect-square rounded-lg overflow-hidden bg-gray-100 cursor-pointer hover:ring-2 hover:ring-purple-400 transition-all"
                onClick={() => onImageClick?.(getImgUrl(img))}>
                <img src={getImgUrl(img)} alt="" className="w-full h-full object-cover" />
              </div>
            ))}
          </div>
        </div>
      )}

      {Object.keys(prefs).length > 0 && (
        <div>
          <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">Preferences</p>
          <div className="space-y-2">
            {Object.entries(prefs).map(([key, val]) => {
              if (!val) return null;
              const label = key.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase());
              return (
                <div key={key} className="flex items-start gap-2">
                  <span className="text-xs font-medium text-gray-600 min-w-[80px]">{label}:</span>
                  <span className="text-xs text-gray-800">
                    {Array.isArray(val) ? val.join(", ") : String(val)}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {(rsvp.notes || prefs.notes) && (
        <div className="p-3 bg-gray-50 rounded-lg">
          <p className="text-xs font-medium text-gray-500 mb-1">Notes</p>
          <p className="text-xs text-gray-700">{rsvp.notes || prefs.notes}</p>
        </div>
      )}
    </div>
  );
}

function KanbanCard({ rsvp, stage, onMove, onViewDetail, onImageClick, onSetPayment }) {
  const prefs = rsvp.preferences || rsvp.intake_answers || {};
  const inspo = rsvp.inspo_images || rsvp.inspiration_images || rsvp.inspo || [];
  const dateStr = rsvp.appointment_date
    ? new Date(rsvp.appointment_date).toLocaleDateString("en-US", { month: "short", day: "numeric" })
    : "TBD";

  const getImgUrl = (img) => typeof img === "string" ? img : (img.url || img.image_url || "");

  return (
    <div
      className="bg-white rounded-lg border border-gray-200 overflow-hidden hover:shadow-md transition-all group relative"
      data-testid={`kanban-card-${rsvp.rsvp_id}`}
    >
      {/* Inspo preview row - always visible */}
      {inspo.length > 0 ? (
        <div className="grid grid-cols-3 h-[52px]">
          {inspo.slice(0, 3).map((img, i) => (
            <div key={i} className="overflow-hidden bg-gray-100">
              <img src={getImgUrl(img)} alt="" className="w-full h-full object-cover" />
            </div>
          ))}
          {Array.from({ length: Math.max(0, 3 - inspo.length) }).map((_, i) => (
            <div key={`e-${i}`} className="bg-gray-50 flex items-center justify-center">
              <ImageIcon className="w-3 h-3 text-gray-200" />
            </div>
          ))}
        </div>
      ) : (
        <div className="h-6 flex items-center justify-center" style={{ backgroundColor: stage.accent + "0D" }}>
          <Sparkles className="w-2.5 h-2.5" style={{ color: stage.accent }} />
        </div>
      )}

      {/* Card body */}
      <div className="p-2.5">
        <div className="flex items-center gap-2">
          <div className="w-6 h-6 rounded-full bg-gray-100 flex items-center justify-center shrink-0 overflow-hidden">
            {rsvp.profile_image ? (
              <img src={rsvp.profile_image} alt="" className="w-6 h-6 rounded-full object-cover" />
            ) : (
              <User className="w-3 h-3 text-gray-400" />
            )}
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-[11px] font-semibold text-gray-900 truncate leading-tight">{rsvp.name}</p>
            {rsvp.service_name && (
              <p className="text-[9px] text-gray-400 truncate leading-tight">{rsvp.service_name}</p>
            )}
            {rsvp.linked_experience_name && (
              <p className="text-[9px] text-purple-500 truncate leading-tight">{rsvp.linked_experience_name}</p>
            )}
          </div>
          <button
            onClick={(e) => { e.stopPropagation(); onViewDetail(rsvp); }}
            className="p-1 rounded hover:bg-gray-100 transition-colors shrink-0"
            data-testid={`kanban-eye-${rsvp.rsvp_id}`}
          >
            <Eye className="w-3.5 h-3.5 text-gray-400 hover:text-gray-700" />
          </button>
        </div>

        {rsvp.payment_status && (
          <span className={`inline-block mt-1 text-[9px] px-1.5 py-0.5 rounded-full ${PAYMENT_OPTIONS.find(p => p.key === rsvp.payment_status)?.color || "bg-stone-100 text-stone-500"}`} data-testid={`kanban-payment-status-${rsvp.rsvp_id}`}>
            {PAYMENT_OPTIONS.find(p => p.key === rsvp.payment_status)?.label || rsvp.payment_status}
          </span>
        )}

        <PrefChips prefs={prefs} />

        <div className="flex items-center justify-between mt-2 pt-1.5 border-t border-gray-50">
          <div className="flex items-center gap-1 text-[9px] text-gray-400">
            <Calendar className="w-2.5 h-2.5" />
            {dateStr}
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="p-0.5 rounded hover:bg-gray-100 opacity-0 group-hover:opacity-100 transition-opacity" data-testid={`kanban-menu-${rsvp.rsvp_id}`}>
                <MoreHorizontal className="w-3 h-3 text-gray-400" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              {STAGES.filter(s => s.key !== deriveStage(rsvp)).map(s => (
                <DropdownMenuItem key={s.key} onClick={() => onMove(rsvp.rsvp_id, s.key)} className="text-xs">
                  <s.icon className="w-3 h-3 mr-2" />
                  Move to {s.label}
                </DropdownMenuItem>
              ))}
              <div className="border-t border-gray-100 my-1" />
              {deriveStage(rsvp) !== "completed" && (
                <DropdownMenuItem onClick={() => onMove(rsvp.rsvp_id, "completed")} className="text-xs text-green-700">
                  <Check className="w-3 h-3 mr-2" />
                  Mark Complete
                </DropdownMenuItem>
              )}
              <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onViewDetail(rsvp); }} className="text-xs">
                <Eye className="w-3 h-3 mr-2" />
                View Preferences
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => onMove(rsvp.rsvp_id, "archived")} className="text-xs text-gray-500">
                <XCircle className="w-3 h-3 mr-2" />
                Cancel / Archive
              </DropdownMenuItem>
              <div className="border-t border-gray-100 my-1" />
              <p className="text-[9px] uppercase tracking-wider text-stone-400 px-2 py-1">Payment</p>
              {PAYMENT_OPTIONS.map(p => (
                <DropdownMenuItem key={p.key} onClick={() => onSetPayment && onSetPayment(rsvp.rsvp_id, p.key)} className="text-xs" data-testid={`kanban-payment-set-${rsvp.rsvp_id}-${p.key}`}>
                  <DollarSign className="w-3 h-3 mr-2" />
                  Mark {p.label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </div>
  );
}

export default function ClientKanban({ rsvps = [], getAuthHeaders, onRefresh }) {
  const [hideArchive, setHideArchive] = useState(false);
  const [detailRsvp, setDetailRsvp] = useState(null);
  const [seeAllStage, setSeeAllStage] = useState(null);
  const [lightboxImg, setLightboxImg] = useState(null);

  const grouped = useMemo(() => {
    const buckets = { invited: [], preferences_shared: [], confirmed: [], completed: [], archived: [] };
    (rsvps || []).forEach(r => {
      const stage = deriveStage(r);
      const bucket = stage === "cancelled" ? "archived" : (buckets[stage] ? stage : "invited");
      buckets[bucket].push(r);
    });
    Object.keys(buckets).forEach(k => { buckets[k] = sortByDate(buckets[k]); });
    return buckets;
  }, [rsvps]);

  const handleMove = useCallback(async (rsvpId, newStage) => {
    try {
      const res = await fetch(`${API}/bookings/${rsvpId}/stage`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", ...getAuthHeaders() },
        body: JSON.stringify({ stage: newStage })
      });
      if (res.ok) {
        toast.success(`Moved to ${STAGES.find(s => s.key === newStage)?.label}`);
        onRefresh?.();
      } else {
        toast.error("Failed to update");
      }
    } catch {
      toast.error("Network error");
    }
  }, [getAuthHeaders, onRefresh]);

  const handleSetPayment = useCallback(async (bookingId, paymentStatus) => {
    try {
      const res = await fetch(`${API}/host/bookings/${bookingId}/payment-status`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", ...getAuthHeaders() },
        body: JSON.stringify({ payment_status: paymentStatus })
      });
      if (res.ok) {
        const label = PAYMENT_OPTIONS.find(p => p.key === paymentStatus)?.label || paymentStatus;
        toast.success(`Marked ${label}`);
        onRefresh?.();
      } else {
        toast.error("Failed to update payment status");
      }
    } catch {
      toast.error("Network error");
    }
  }, [getAuthHeaders, onRefresh]);

  const visibleStages = hideArchive ? STAGES.filter(s => s.key !== "archived") : STAGES;

  return (
    <div data-testid="client-kanban">
      {/* Controls */}
      <div className="flex items-center justify-end mb-3 gap-2">
        <button
          onClick={() => setHideArchive(!hideArchive)}
          className="flex items-center gap-1.5 text-xs text-gray-400 hover:text-gray-700 px-2 py-1 rounded-lg hover:bg-gray-100 transition-colors"
          data-testid="toggle-archive-btn"
        >
          {hideArchive ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}
          {hideArchive ? "Show Archive" : "Hide Archive"}
        </button>
      </div>

      {/* Horizontal Kanban columns */}
      <div className="flex gap-3 overflow-x-auto pb-4" style={{ minHeight: 320 }}>
        {visibleStages.map(stage => {
          const items = grouped[stage.key] || [];
          const StageIcon = stage.icon;

          return (
            <div
              key={stage.key}
              className="flex-shrink-0 rounded-xl border border-gray-200 bg-gray-50/50 flex flex-col"
              style={{ width: 240 }}
              data-testid={`kanban-col-${stage.key}`}
            >
              {/* Column header */}
              <div className={`flex items-center gap-2 px-3 py-2.5 rounded-t-xl ${stage.headerBg} border-b border-gray-200`}>
                <StageIcon className="w-3.5 h-3.5" style={{ color: stage.accent }} />
                <span className="text-[10px] font-bold uppercase tracking-wider text-gray-600">{stage.label}</span>
                <Badge variant="secondary" className={`${stage.badge} text-[9px] px-1.5 py-0 ml-auto`}>
                  {items.length}
                </Badge>
              </div>

              {/* Cards */}
              <div className="flex-1 p-2 space-y-2 overflow-y-auto" style={{ maxHeight: 460 }}>
                {items.length > 0 ? (
                  items.map(r => (
                    <KanbanCard
                      key={r.rsvp_id}
                      rsvp={r}
                      stage={stage}
                      onMove={handleMove}
                      onViewDetail={setDetailRsvp}
                      onImageClick={setLightboxImg}
                      onSetPayment={handleSetPayment}
                    />
                  ))
                ) : (
                  <div className="rounded-lg border-2 border-dashed border-gray-200 p-4 text-center" style={{ minHeight: 60 }}>
                    <p className="text-[10px] text-gray-300">No clients</p>
                  </div>
                )}
              </div>

              {/* See all footer */}
              {items.length > 3 && (
                <div className="px-3 py-2 border-t border-gray-200">
                  <button
                    onClick={() => setSeeAllStage(stage)}
                    className="flex items-center justify-center gap-1 w-full text-[10px] text-gray-500 hover:text-gray-800 py-1 rounded hover:bg-gray-100"
                    data-testid={`see-all-${stage.key}`}
                  >
                    <Grid3X3 className="w-3 h-3" />
                    See all {items.length}
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Preference detail dialog - Iter 9.40 Pass I unified into AttendeeCardModal */}
      <AttendeeCardModal
        open={!!detailRsvp}
        onOpenChange={(v) => !v && setDetailRsvp(null)}
        rsvp={detailRsvp}
        hostEventTitle={detailRsvp?.event_name || detailRsvp?.service_name || "Preference Card"}
        onStatusChange={(id, stage) => { handleMove(id, stage); setDetailRsvp(null); }}
        onCantMakeIt={() => { if (detailRsvp) { handleMove(detailRsvp.rsvp_id, "archived"); setDetailRsvp(null); } }}
      />

      {/* See all dialog */}
      <Dialog open={!!seeAllStage} onOpenChange={() => setSeeAllStage(null)}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-sm">
              {seeAllStage && <seeAllStage.icon className="w-4 h-4" style={{ color: seeAllStage?.accent }} />}
              {seeAllStage?.label}
              {seeAllStage && <Badge className={`ml-2 ${seeAllStage.badge} text-xs`}>{(grouped[seeAllStage.key] || []).length}</Badge>}
            </DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mt-3">
            {seeAllStage && (grouped[seeAllStage.key] || []).map(r => (
              <KanbanCard
                key={r.rsvp_id}
                rsvp={r}
                stage={seeAllStage}
                onMove={handleMove}
                onViewDetail={(rv) => { setSeeAllStage(null); setDetailRsvp(rv); }}
                onImageClick={setLightboxImg}
                onSetPayment={handleSetPayment}
              />
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Image lightbox */}
      {lightboxImg && (
        <div className="fixed inset-0 z-[100] bg-black/80 flex items-center justify-center p-4 cursor-pointer" onClick={() => setLightboxImg(null)} data-testid="kanban-lightbox">
          <img src={lightboxImg} alt="Inspiration" className="max-w-full max-h-full object-contain rounded-lg shadow-2xl" />
        </div>
      )}
    </div>
  );
}
